// SPDX-License-Identifier: AGPL-3.0-or-later
// Group expenses in the app: recurring bills as dated charges, balances,
// matching incoming e-Transfers as repayments, and keeping money fronted for
// others out of your own spending.
import { addDays, daysBetween, groupBalances, occurrences, settleUp, splitAmount, type Cents, type IsoDate, type Repayment } from "@almanac/domain";
import { FRONTED } from "./categories.js";
import { MATCH_DAYS, MATCH_SHARE } from "./commitments.js";
import { ME, type Group, type Transaction } from "./model.js";

export { FRONTED };

/** One expense, or one due date of a recurring one. */
export interface Charge {
  id: string;
  date: IsoDate;
  description: string;
  amount: Cents;
  paidBy: string;
  shares: ReadonlyMap<string, Cents>;
  /** The transaction that paid it: chosen for a one-off expense, found for a recurring one. */
  txId: string | null;
  recurringId: string | null;
}

export interface GroupView {
  group: Group;
  /** Newest first. */
  charges: Charge[];
  balances: ReadonlyMap<string, Cents>;
  /** The fewest transfers that settle the group. */
  transfers: Repayment[];
}

/**
 * Every charge up to today. A recurring bill you pay is paired with the
 * transaction that paid it the way events are: within 10% of the amount and
 * 5 days of the date, closest first, each transaction once.
 */
export function charges(group: Group, transactions: readonly Transaction[], today: IsoDate): Charge[] {
  const list: Charge[] = group.expenses.map((e) => ({
    id: e.id,
    date: e.date,
    description: e.description,
    amount: e.amount,
    paidBy: e.paidBy,
    shares: splitAmount(e.amount, e.split),
    txId: e.txId,
    recurringId: null,
  }));
  const repeating: Charge[] = group.recurring.flatMap((r) =>
    occurrences({ date: r.start, repeat: r.repeat, until: r.until }, r.start, today).map((date) => ({
      id: `${r.id}|${date}`,
      date,
      description: r.description,
      amount: r.amount,
      paidBy: r.paidBy,
      shares: splitAmount(r.amount, r.split),
      txId: null,
      recurringId: r.id,
    })),
  );
  const used = new Set(list.flatMap((c) => (c.txId ? [c.txId] : [])));
  const mine = repeating.filter((c) => c.paidBy === ME);
  if (mine.length > 0) {
    const from = addDays(mine.reduce((m, c) => (c.date < m ? c.date : m), today), -MATCH_DAYS);
    const pairs: { c: Charge; t: Transaction; gap: number }[] = [];
    for (const t of transactions) {
      if (t.amount >= 0 || t.date < from || used.has(t.id)) continue;
      for (const c of mine) {
        const gap = Math.abs(daysBetween(c.date, t.date));
        if (gap <= MATCH_DAYS && Math.abs(-t.amount - c.amount) <= c.amount * MATCH_SHARE) pairs.push({ c, t, gap });
      }
    }
    pairs.sort((a, b) => a.gap - b.gap || Math.abs(-a.t.amount - a.c.amount) - Math.abs(-b.t.amount - b.c.amount));
    for (const { c, t } of pairs) {
      if (c.txId || used.has(t.id)) continue;
      c.txId = t.id;
      used.add(t.id);
    }
  }
  return [...list, ...repeating].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.id.localeCompare(b.id)));
}

export function groupView(group: Group, transactions: readonly Transaction[], today: IsoDate): GroupView {
  const list = charges(group, transactions, today);
  const balances = groupBalances(
    group.members.map((m) => m.id),
    list,
    group.payments,
  );
  return { group, charges: list, balances, transfers: settleUp(balances) };
}

/** What each member owes you, from the settle-up plan. */
export function owedToMe(view: GroupView): Map<string, Cents> {
  return new Map(view.transfers.filter((t) => t.to === ME).map((t) => [t.from, t.amount]));
}

/** What you owe each member, from the settle-up plan. */
export function iOwe(view: GroupView): Map<string, Cents> {
  return new Map(view.transfers.filter((t) => t.from === ME).map((t) => [t.to, t.amount]));
}

/**
 * For each transaction that paid for a group, the part others owe back,
 * which is not your spending. Never more than the transaction itself.
 */
export function frontedByTransaction(groups: readonly Group[], transactions: readonly Transaction[], today: IsoDate): Map<string, Cents> {
  const byId = new Map(transactions.map((t) => [t.id, t]));
  const out = new Map<string, Cents>();
  for (const group of groups) {
    for (const c of charges(group, transactions, today)) {
      const t = c.txId ? byId.get(c.txId) : undefined;
      if (!t || c.paidBy !== ME || t.amount >= 0) continue;
      let others = 0;
      for (const [m, share] of c.shares) if (m !== ME) others += share;
      out.set(t.id, Math.min(-t.amount, (out.get(t.id) ?? 0) + others));
    }
  }
  return out;
}

/**
 * Transactions as the budget counts them: a payment that covered others is
 * split into your share (in its category) and the fronted part, which is
 * not spending. Returns the same array when nothing was fronted.
 */
export function withoutFronted(transactions: Transaction[], fronted: ReadonlyMap<string, Cents>): Transaction[] {
  if (fronted.size === 0) return transactions;
  return transactions.flatMap((t) => {
    const f = fronted.get(t.id);
    if (!f) return [t];
    return [
      { ...t, amount: t.amount + f },
      { ...t, id: `${t.id}|fronted`, amount: -f, category: FRONTED, fingerprint: `${t.fingerprint}|fronted` },
    ];
  });
}

const TRANSFER = /e-?\s?transfer|interac|virement|etfr|e-tfr/i;
/** How far back incoming transfers are offered as repayments. */
const OFFER_DAYS = 60;

export interface Offer {
  groupId: string;
  memberId: string;
  transaction: Transaction;
}

/**
 * Incoming e-Transfers that look like a member paying you back: from the
 * last 60 days, not already recorded or declined, and either naming the
 * member or for exactly what they owe you.
 */
export function repaymentOffers(views: readonly GroupView[], transactions: readonly Transaction[], today: IsoDate): Offer[] {
  const taken = new Set(views.flatMap((v) => [...v.group.payments.flatMap((p) => (p.txId ? [p.txId] : [])), ...v.group.declined]));
  const since = addDays(today, -OFFER_DAYS);
  const offers: Offer[] = [];
  for (const t of transactions) {
    if (t.amount <= 0 || t.date < since || taken.has(t.id) || !TRANSFER.test(t.description)) continue;
    const text = t.description.toLowerCase();
    const match = views
      .flatMap((v) => [...owedToMe(v)].map(([memberId, owed]) => ({ v, memberId, owed })))
      .map((o) => {
        const name = o.v.group.members.find((m) => m.id === o.memberId)?.name ?? "";
        const first = name.split(/\s+/)[0]!.toLowerCase();
        const named = first.length >= 2 && text.includes(first);
        return { ...o, score: (named ? 2 : 0) + (o.owed === t.amount ? 1 : 0) };
      })
      .filter((o) => o.score > 0)
      .sort((a, b) => b.score - a.score)[0];
    if (match) offers.push({ groupId: match.v.group.id, memberId: match.memberId, transaction: t });
  }
  return offers;
}

/**
 * The expenses behind what a member owes you, newest first, up to the amount
 * owed: the ones you paid that include them.
 */
export function owedItems(view: GroupView, memberId: string, owed: Cents): { charge: Charge; share: Cents }[] {
  const items: { charge: Charge; share: Cents }[] = [];
  let left = owed;
  for (const charge of view.charges) {
    if (left <= 0) break;
    const share = charge.shares.get(memberId) ?? 0;
    if (charge.paidBy !== ME || share <= 0) continue;
    items.push({ charge, share });
    left -= share;
  }
  return items;
}
